import { Controller } from "@hotwired/stimulus";
import Chartkick from "chartkick";

const STORAGE_KEY = "zhacloud-theme-mode";
const LEGACY_STORAGE_KEY = "ohimy-theme-mode";
const MODES = ["light", "dark", "system"];
const THEME_ICON_CLASSES = {
  light: "lucide--sun",
  dark: "lucide--moon",
  system: "lucide--monitor"
};

export default class extends Controller {
  connect() {
    this.onThemeControlClick = this.onThemeControlClick.bind(this);
    this.onSystemThemeChanged = this.onSystemThemeChanged.bind(this);
    this.refreshChartTheme = this.refreshChartTheme.bind(this);

    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.currentMode = this.readMode();

    this.applyMode(this.currentMode);
    document.addEventListener("click", this.onThemeControlClick);
    this.mediaQuery.addEventListener("change", this.onSystemThemeChanged);
    document.addEventListener("turbo:load", this.refreshChartTheme);
    window.addEventListener("chartkick:load", this.refreshChartTheme);
  }

  disconnect() {
    document.removeEventListener("click", this.onThemeControlClick);
    this.mediaQuery?.removeEventListener("change", this.onSystemThemeChanged);
    document.removeEventListener("turbo:load", this.refreshChartTheme);
    window.removeEventListener("chartkick:load", this.refreshChartTheme);
  }

  onThemeControlClick(event) {
    const control = event.target.closest("[data-theme-control]");
    if (!control) return;

    const nextMode = control.dataset.themeControl;
    if (!MODES.includes(nextMode)) return;

    this.currentMode = nextMode;
    this.persistMode(nextMode);
    this.applyMode(nextMode);
  }

  onSystemThemeChanged() {
    if (this.currentMode === "system") {
      this.applyMode("system");
    }
  }

  applyMode(mode) {
    const root = document.documentElement;
    this.element.querySelectorAll("[data-theme-control]").forEach((control) => {
      control.setAttribute("aria-pressed", String(control.dataset.themeControl === mode));
    });
    this.updateThemeToggleIcon(mode);

    const theme = mode === "system" ? (this.mediaQuery.matches ? "dark" : "light") : mode;
    root.setAttribute("data-theme", theme);

    this.refreshChartTheme();
  }

  refreshChartTheme() {
    const style = getComputedStyle(document.documentElement);
    const color = (token) => style.getPropertyValue(token).trim();
    const muted = color("--text-muted");
    const text = color("--color-base-content");
    const border = color("--color-base-300");

    Chartkick.eachChart((chart) => {
      const canvasChart = chart.getChartObject();
      if (!canvasChart || !document.contains(chart.getElement())) return;

      const options = canvasChart.options;
      options.color = muted;
      options.plugins.legend.labels.color = muted;
      options.plugins.title.color = text;
      Object.assign(options.plugins.tooltip, {
        backgroundColor: color("--color-base-200"),
        titleColor: text,
        bodyColor: text,
        borderColor: border,
        borderWidth: 1
      });
      Object.values(options.scales || {}).forEach((axis) => {
        axis.ticks.color = muted;
        axis.title.color = text;
        axis.grid.color = border;
        axis.border.color = border;
      });
      canvasChart.update("none");
    });
  }

  updateThemeToggleIcon(mode) {
    const icon = this.element.querySelector("[data-theme-toggle-icon]");
    if (!icon) return;

    icon.classList.remove(...Object.values(THEME_ICON_CLASSES));
    icon.classList.add(THEME_ICON_CLASSES[mode]);
  }

  readMode() {
    let value = null;
    try {
      value = window.localStorage.getItem(STORAGE_KEY) || window.localStorage.getItem(LEGACY_STORAGE_KEY);
    } catch (_error) {
      value = null;
    }
    return MODES.includes(value) ? value : "dark";
  }

  persistMode(mode) {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch (_error) {
      // ignore storage errors and keep current runtime mode
    }
  }
}
